/**
 * @license Copyright 2024 by vjoon GmbH. All rights reserved.
 * Kieler Straße 103-107, D-25474 Bönningstedt
 */

// The editor creator to use.
import { BalloonEditor as BalloonEditorBase } from '@ckeditor/ckeditor5-editor-balloon';

import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat';
import { Bold, Italic } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { Heading, HeadingButtonsUI, Title } from '@ckeditor/ckeditor5-heading';
import { Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload, ImageResize } from '@ckeditor/ckeditor5-image';
import { Indent } from '@ckeditor/ckeditor5-indent';
import { Link } from '@ckeditor/ckeditor5-link';
import { List, ListProperties } from '@ckeditor/ckeditor5-list';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph, ParagraphButtonUI } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { Table, TableToolbar } from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';

// additional modules
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { TrackChanges, TrackChangesData } from '@ckeditor/ckeditor5-track-changes';
import { Comments } from '@ckeditor/ckeditor5-comments';


// vjoon customize
import { Autosave } from '@ckeditor/ckeditor5-autosave';
import DeleteButton from '@vjoon/ckeditor5-vj-toolbar/src/deleteButton';
import VjBlockToolbar from '@vjoon/ckeditor5-vj-toolbar/src/vjBlockToolbar';
import PlainData from '@vjoon/ckeditor5-vj-plaindata/src/plainData';
import InsertContent from '@vjoon/ckeditor5-vj-insertcontent/src/insertContent';

/**
 * @internal
 */
import '../theme/vjoon.css';

export default class BalloonEditor extends BalloonEditorBase {
	public static override builtinPlugins = [
		Essentials,
		Autoformat,
		Bold,
		Italic,
		BlockQuote,
		Heading,
		Image,
		ImageCaption,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		Link,
		List,
		ListProperties,
		MediaEmbed,
		Paragraph,
		PasteFromOffice,
		Table,
		TableToolbar,
		TextTransformation,

		ParagraphButtonUI,
		HeadingButtonsUI,
		Autosave,
		ImageResize,
		RemoveFormat,
		TrackChanges,
		TrackChangesData,
		Comments,
		Title,
		FindAndReplace,
		VjBlockToolbar,
		PlainData,
		InsertContent,
		DeleteButton
	];

	// Editor configuration.
	public static override defaultConfig = {
		blockToolbar: [
			'heading',
			'|',
			'bulletedList',
			'numberedList',
			'|',
			'indent',
			'outdent',
			'|',
			'uploadImage',
			'blockQuote',
			'insertTable',
			'mediaEmbed',
			'|',
			'undo',
			'redo',
			'|',
			'trackChanges',
			'findAndReplace',
			'comment',
			'commentsArchive'
		],
		toolbar: {
			items: [
				'bold',
				'italic',
				'link'
			]
		},
		image: {
			toolbar: [
				'imageStyle:block',
				'imageStyle:side',
				'|',
				'toggleImageCaption',
				'imageTextAlternative'
			],
			image: {
				insert: {
					type: 'block'
				}
			}
		},
		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells'
			]
		},
		comments: {
			editorConfig: {
				extraPlugins: [ Autoformat, Bold, Italic, List ]
			}
		},
		trackChanges: {
			disableComments: false,
			// Do not track styling and formatting changes (default is `true`).
			trackFormatChanges: 'default' as const,
			mergeNestedSuggestions: true
		},
		list: {
			multiBlock: true, // Turn off to prevent multi block support (enabled by default).
			properties: {
				styles: false,
				startIndex: true,
				reversed: true
			}
		},
		// This value must be kept in sync with the language defined in webpack.config.js.
		language: 'en',
		// use CKEditor 5 Track Changes plugin - Annual Fee
		licenseKey: 'c3dZVUlKbnQ4aG5Hd0dGKzBjaEs0REYvaUYzSzJ2RW9GVmRYaTdHNzZvYk4tTWpBeU5UQTFNVEU9'
	};
}
